<template>
  <form class="secret-editor" @submit.prevent="submit">
    <div class="secret-editor__grid">
      <div class="secret-editor__field">
        <label class="secret-editor__label" for="secret-name">Nombre</label>
        <input
          id="secret-name"
          v-model.trim="form.name"
          class="secret-editor__input"
          type="text"
          placeholder="SII, Banco, ERP..."
        >
      </div>

      <div class="secret-editor__field">
        <label class="secret-editor__label" for="secret-category">Categoria</label>
        <input
          id="secret-category"
          v-model.trim="form.category"
          class="secret-editor__input"
          type="text"
          placeholder="Impuestos, Banco, Correo..."
        >
      </div>

      <div class="secret-editor__field">
        <label class="secret-editor__label" for="secret-username">Usuario</label>
        <input
          id="secret-username"
          v-model.trim="form.username"
          class="secret-editor__input"
          type="text"
          placeholder="usuario o correo asociado"
        >
      </div>

      <div class="secret-editor__field">
        <label class="secret-editor__label" for="secret-owner">Responsable</label>
        <input
          id="secret-owner"
          v-model.trim="form.owner"
          class="secret-editor__input"
          type="text"
          placeholder="Area o persona responsable"
        >
      </div>

      <div class="secret-editor__field secret-editor__field--full">
        <label class="secret-editor__label" for="secret-value">Valor</label>
        <div class="secret-editor__secret-row">
          <input
            id="secret-value"
            v-model.trim="form.secret"
            class="secret-editor__input"
            :type="showSecret ? 'text' : 'password'"
            placeholder="Valor del secreto"
            autocomplete="new-password"
          >
          <button
            class="secret-editor__toggle"
            type="button"
            @click="showSecret = !showSecret"
          >
            {{ showSecret ? 'Ocultar' : 'Mostrar' }}
          </button>
        </div>
      </div>

      <div class="secret-editor__field secret-editor__field--full">
        <label class="secret-editor__toggle-check">
          <input v-model="form.needs_rotation" type="checkbox">
          <span>Marcar como pendiente de rotacion</span>
        </label>
      </div>
    </div>

    <p v-if="error" class="secret-editor__error">{{ error }}</p>

    <footer class="secret-editor__actions">
      <button class="secret-editor__ghost" type="button" :disabled="saving" @click="$emit('cancel')">
        Cancelar
      </button>

      <button class="secret-editor__primary" type="submit" :disabled="saving">
        {{ saving ? 'Guardando...' : submitLabel }}
      </button>
    </footer>
  </form>
</template>

<script>
import companyAccessService from '@/services/company/companyAccess.service'

const defaultForm = () => ({
  id: null,
  name: '',
  category: '',
  username: '',
  owner: '',
  secret: '',
  needs_rotation: false
})

export default {
  name: 'SecretEditor',
  props: {
    value: {
      type: Object,
      default: null
    },
    companyId: {
      type: [String, Number],
      default: null
    },
    personId: {
      type: [String, Number],
      default: null
    }
  },
  data () {
    return {
      form: defaultForm(),
      saving: false,
      error: '',
      showSecret: false
    }
  },
  computed: {
    submitLabel () {
      return this.form.id ? 'Guardar cambios' : 'Crear acceso'
    }
  },
  watch: {
    value: {
      immediate: true,
      handler (newValue) {
        this.form = {
          ...defaultForm(),
          ...(newValue || {})
        }
        if (newValue && !newValue.secret) {
          this.form.secret = ''
        }
      }
    }
  },
  methods: {
    validate () {
      if (!this.form.name) {
        this.error = 'El nombre es obligatorio.'
        return false
      }

      if (!this.form.secret && !this.form.id) {
        this.error = 'El valor del secreto es obligatorio.'
        return false
      }

      this.error = ''
      return true
    },
    normalizePayload () {
      return {
        id: this.form.id,
        company_id: this.companyId !== null && this.companyId !== undefined && this.companyId !== '' ? Number(this.companyId) : null,
        person_id: this.personId !== null && this.personId !== undefined && this.personId !== '' ? Number(this.personId) : null,
        name: this.form.name,
        category: this.form.category,
        username: this.form.username,
        owner: this.form.owner,
        secret: this.form.secret,
        needs_rotation: Boolean(this.form.needs_rotation)
      }
    },
    async submit () {
      if (!this.validate()) return

      this.saving = true
      this.error = ''

      try {
        const payload = this.normalizePayload()
        const saved = this.form.id
          ? await companyAccessService.updateSecret(payload)
          : await companyAccessService.createSecret(payload)

        this.$emit('saved', saved)
      } catch (err) {
        this.error = err && err.message ? err.message : 'No fue posible guardar el acceso.'
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.secret-editor {
  display: grid;
  gap: 1rem;
}

.secret-editor__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.9rem;
}

.secret-editor__field {
  display: grid;
  gap: 0.38rem;
}

.secret-editor__field--full {
  grid-column: span 2;
}

.secret-editor__label {
  color: #4b3b2f;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.secret-editor__input {
  min-height: 2.9rem;
  padding: 0.7rem 0.85rem;
  border: 1px solid rgba(102, 81, 57, 0.14);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.76);
  color: #241c16;
  width: 100%;
}

.secret-editor__secret-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.5rem;
}

.secret-editor__toggle {
  min-height: 2.9rem;
  padding: 0 0.9rem;
  border: 1px solid rgba(102, 81, 57, 0.12);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.72);
  color: #392d22;
}

.secret-editor__toggle-check {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  color: #4a3a2d;
}

.secret-editor__error {
  margin: 0;
  color: #a24444;
  font-size: 0.9rem;
}

.secret-editor__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.55rem;
}

.secret-editor__ghost,
.secret-editor__primary {
  min-height: 2.65rem;
  padding: 0 0.95rem;
  border-radius: 999px;
}

.secret-editor__ghost {
  border: 1px solid rgba(102, 81, 57, 0.12);
  background: rgba(255, 255, 255, 0.72);
  color: #392d22;
}

.secret-editor__primary {
  border: 0;
  background: linear-gradient(135deg, #251d16 0%, #4c3c2d 100%);
  color: #f7f0e5;
}

@media (max-width: 640px) {
  .secret-editor__grid {
    grid-template-columns: 1fr;
  }

  .secret-editor__field--full {
    grid-column: span 1;
  }

  .secret-editor__secret-row {
    grid-template-columns: 1fr;
  }

  .secret-editor__actions {
    flex-direction: column;
  }
}
</style>
